/**
 * Pure runtime/device grouping logic for `ActiveThreadsSection`.
 *
 * Extracted from `ActiveThreadsSection.tsx` (the JSX component that consumes
 * `buildRuntimeProjectGroups`, `resolveRuntimeEnvironmentLabel`,
 * `resolveCanonicalProjectEmptyState`, `resolveCloudProjectStatus`, and the
 * `UNKNOWN_PROJECT_DISPLAY` fallback). This half of the file had no JSX and no
 * component-local state — it was pure data transformation reachable purely
 * through props/arguments — so it moves as-is with no behaviour change.
 */
import type {
  CmmdDeviceRegistryDevice,
  CmmdDeviceRegistryRuntime,
  CmmdRuntimeInventoryRuntime,
  EnvironmentId,
} from "@cmmd-forge/contracts";
import { refreshCmmdForgeRuntimeList } from "../../cmmd/cmmdForgeRuntimeListResource";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { resolveCloudGroupState } from "./cloudRuntimeRowState.logic";
import type { EnvironmentKind } from "./environmentKind";
import {
  buildRegisteredDesktopRuntimeMap,
  type RegisteredDesktopRuntimeGroup,
} from "./registeredDesktopRuntimeMap";
import type { RuntimeGroupStatus } from "./RuntimeGroupStatusIndicator";
import type {
  ProjectGroupedSidebarCloudStatusModel,
  ProjectGroupedSidebarEmptyStateModel,
} from "./ProjectGroupedSidebarEmptyState";
import type { ProjectDisplayEntry } from "./projectDisplayIndex";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import { cloneProjectForRuntimeEnvironment } from "./cloneProjectForRuntimeEnvironment";
import {
  isExecutableRuntimeEntry,
  type SavedEnvironmentRuntimeEntry,
} from "./runtimeEnvironmentClassification";

interface RuntimeProjectGroup {
  readonly environmentId: EnvironmentId | null;
  readonly device: CmmdDeviceRegistryDevice | null;
  readonly label: string;
  readonly kind: EnvironmentKind;
  readonly projects: readonly SidebarProjectSnapshot[];
  readonly showKindIcon: boolean;
  readonly status?: RuntimeGroupStatus | undefined;
}

interface RuntimeProjectPartition {
  readonly localProjects: readonly SidebarProjectSnapshot[];
  readonly remoteProjectGroups: ReadonlyMap<EnvironmentId, readonly SidebarProjectSnapshot[]>;
}

export interface RuntimeEnvironmentLabelContext {
  readonly savedEnvironmentRegistry: Readonly<
    Record<string, { readonly label?: string } | undefined>
  >;
  readonly savedEnvironmentRuntimeById: Readonly<Record<string, SavedEnvironmentRuntimeEntry>>;
  readonly registeredDesktopRuntimeById: ReadonlyMap<EnvironmentId, RegisteredDesktopRuntimeGroup>;
  readonly inventoryRuntimeById: ReadonlyMap<string, CmmdRuntimeInventoryRuntime>;
}

export function resolveCanonicalProjectEmptyState(input: {
  readonly isBootstrappingLocal: boolean;
  readonly isElectron: boolean;
}): ProjectGroupedSidebarEmptyStateModel {
  return input.isBootstrappingLocal
    ? { state: "loading" }
    : { state: "empty", label: input.isElectron ? "No projects yet" : "No projects" };
}

export function resolveCloudProjectStatus(
  cloudState: ReturnType<typeof resolveCloudGroupState>,
): ProjectGroupedSidebarCloudStatusModel | null {
  if (cloudState.kind === "loading") return { state: "loading" };
  if (cloudState.kind === "stale-rows") {
    return {
      state: "stale",
      label: "CMMD offline — showing cached data",
      actionLabel: "Retry",
      onAction: refreshCmmdForgeRuntimeList,
    };
  }
  if (cloudState.kind === "error-no-rows") {
    return {
      state: "error",
      label: "Cannot reach CMMD",
      actionLabel: "Retry",
      onAction: refreshCmmdForgeRuntimeList,
    };
  }
  return null;
}

function resolveSavedEnvironmentLabel(input: {
  readonly environmentId: EnvironmentId;
  readonly savedEnvironmentRegistry: Readonly<
    Record<string, { readonly label?: string } | undefined>
  >;
  readonly savedEnvironmentRuntimeById: Readonly<
    Record<string, { readonly descriptor?: { readonly label?: string } | null } | undefined>
  >;
}): string {
  return (
    input.savedEnvironmentRuntimeById[input.environmentId]?.descriptor?.label ??
    input.savedEnvironmentRegistry[input.environmentId]?.label ??
    input.environmentId
  );
}

/**
 * What a row shows when neither the live store nor CMMD's replica knows the
 * thread's project. The Activity card renders "Unknown project" from a null
 * name rather than dropping the row, so the gap is reported, not hidden.
 */
export const UNKNOWN_PROJECT_DISPLAY: ProjectDisplayEntry = { name: null, cwd: null };

function resolveRuntimeEnvironmentKind(input: {
  readonly label: string;
  readonly platformOs?: string | null;
}): EnvironmentKind {
  const label = input.label.toLowerCase();
  if (label.includes("book") || label.includes("laptop")) return "remote-laptop";
  return "remote-desktop";
}

function addRemoteProjectGroup(
  groups: Map<EnvironmentId, SidebarProjectSnapshot[]>,
  environmentId: EnvironmentId,
  project: SidebarProjectSnapshot,
): void {
  const scopedProject = cloneProjectForRuntimeEnvironment(project, environmentId);
  if (!scopedProject) return;
  const existing = groups.get(environmentId);
  if (existing) existing.push(scopedProject);
  else groups.set(environmentId, [scopedProject]);
}

function partitionRuntimeProjects(
  projects: readonly SidebarProjectSnapshot[],
): RuntimeProjectPartition {
  const localProjects: SidebarProjectSnapshot[] = [];
  const remoteProjectGroups = new Map<EnvironmentId, SidebarProjectSnapshot[]>();

  for (const project of projects) {
    const localMembers = project.worktreeMembers.filter(
      (member) => member.environmentRole === "local",
    );
    if (localMembers.length > 0 || project.worktreeMembers.length === 0) {
      const localEnvironmentId = localMembers[0]?.environmentId ?? project.environmentId;
      const localProject = cloneProjectForRuntimeEnvironment(
        project,
        localEnvironmentId,
        "local-with-cloud",
      );
      if (localProject) localProjects.push(localProject);
    }

    const remoteEnvironmentIds = new Set(
      project.worktreeMembers
        .filter((member) => member.environmentRole === "remote")
        .map((member) => member.environmentId),
    );
    for (const environmentId of remoteEnvironmentIds) {
      addRemoteProjectGroup(remoteProjectGroups, environmentId, project);
    }
  }

  return { localProjects, remoteProjectGroups };
}

function isCloudRuntimeEnvironmentId(
  environmentId: EnvironmentId,
  cloudRuntimeEnvironmentIds: ReadonlySet<string>,
): boolean {
  return (
    cloudRuntimeEnvironmentIds.has(environmentId) || classifyEnvironmentId(environmentId).isCloud
  );
}

function getSavedExecutableEnvironmentIds(input: {
  readonly savedEnvironmentRegistry: Readonly<
    Record<string, { readonly label?: string } | undefined>
  >;
  readonly savedEnvironmentRuntimeById: Readonly<Record<string, SavedEnvironmentRuntimeEntry>>;
}): readonly EnvironmentId[] {
  return Object.keys(input.savedEnvironmentRegistry).filter((environmentId) =>
    isExecutableRuntimeEntry(input.savedEnvironmentRuntimeById[environmentId]),
  ) as EnvironmentId[];
}

function buildRemoteEnvironmentIdSet(input: {
  readonly remoteProjectGroups: ReadonlyMap<EnvironmentId, readonly SidebarProjectSnapshot[]>;
  readonly savedExecutableEnvironmentIds: readonly EnvironmentId[];
  readonly registeredDesktopRuntimeById: ReadonlyMap<EnvironmentId, RegisteredDesktopRuntimeGroup>;
  readonly cloudRuntimeEnvironmentIds: ReadonlySet<string>;
}): ReadonlySet<EnvironmentId> {
  const environmentIds = new Set<EnvironmentId>();
  const savedExecutableEnvironmentIds = new Set(input.savedExecutableEnvironmentIds);
  for (const environmentId of input.remoteProjectGroups.keys()) {
    if (
      !isCloudRuntimeEnvironmentId(environmentId, input.cloudRuntimeEnvironmentIds) &&
      (input.registeredDesktopRuntimeById.has(environmentId) ||
        savedExecutableEnvironmentIds.has(environmentId))
    ) {
      environmentIds.add(environmentId);
    }
  }
  for (const environmentId of savedExecutableEnvironmentIds) {
    if (!isCloudRuntimeEnvironmentId(environmentId, input.cloudRuntimeEnvironmentIds)) {
      environmentIds.add(environmentId);
    }
  }
  for (const environmentId of input.registeredDesktopRuntimeById.keys()) {
    environmentIds.add(environmentId);
  }
  return environmentIds;
}

export function resolveRuntimeEnvironmentLabel(
  environmentId: EnvironmentId,
  context: RuntimeEnvironmentLabelContext,
): string {
  return (
    context.registeredDesktopRuntimeById.get(environmentId)?.label ??
    context.inventoryRuntimeById.get(environmentId)?.runtime.displayName ??
    resolveSavedEnvironmentLabel({
      environmentId,
      savedEnvironmentRegistry: context.savedEnvironmentRegistry,
      savedEnvironmentRuntimeById: context.savedEnvironmentRuntimeById,
    })
  );
}

function sortRuntimeEnvironmentIds(
  environmentIds: ReadonlySet<EnvironmentId>,
  context: RuntimeEnvironmentLabelContext,
): readonly EnvironmentId[] {
  return [...environmentIds].toSorted((left, right) =>
    resolveRuntimeEnvironmentLabel(left, context).localeCompare(
      resolveRuntimeEnvironmentLabel(right, context),
    ),
  );
}

function resolveRuntimeGroupStatus(input: {
  readonly registeredRuntime: RegisteredDesktopRuntimeGroup | undefined;
  readonly inventoryRuntime: CmmdRuntimeInventoryRuntime | undefined;
}): RuntimeGroupStatus | undefined {
  if (
    input.inventoryRuntime?.inventoryStatus === "offline" ||
    input.registeredRuntime?.runtime.status === "offline"
  ) {
    return "offline";
  }
  if (
    input.inventoryRuntime?.inventoryStatus === "sync_pending" ||
    (input.registeredRuntime !== undefined && input.inventoryRuntime === undefined)
  ) {
    return "sync_pending";
  }
  return undefined;
}

export function buildRuntimeProjectGroups(input: {
  readonly projects: readonly SidebarProjectSnapshot[];
  readonly showLocalEnvironment: boolean;
  readonly localEnvironmentLabel: string | null;
  readonly savedEnvironmentRegistry: Readonly<
    Record<string, { readonly label?: string } | undefined>
  >;
  readonly savedEnvironmentRuntimeById: Readonly<Record<string, SavedEnvironmentRuntimeEntry>>;
  readonly registeredRuntimes?: readonly CmmdDeviceRegistryRuntime[];
  readonly registeredDevices?: Parameters<
    typeof buildRegisteredDesktopRuntimeMap
  >[0]["registeredDevices"];
  readonly registeredHosts?: Parameters<
    typeof buildRegisteredDesktopRuntimeMap
  >[0]["registeredHosts"];
  readonly currentRegisteredRuntimeId?: string | null;
  readonly currentRegisteredDeviceKey?: string | null;
  readonly runtimeInventory?: readonly CmmdRuntimeInventoryRuntime[];
  /**
   * Environment ids of connected Forge Cloud runtimes. These are shown under the
   * CLOUD group, so they must be excluded from the per-device/workspace groups —
   * otherwise a connected cloud VM renders as an empty phantom "No projects yet"
   * workspace (labeled by its raw runtime/machine id).
   */
  readonly cloudRuntimeEnvironmentIds: ReadonlySet<string>;
}): readonly RuntimeProjectGroup[] {
  const { localProjects, remoteProjectGroups } = partitionRuntimeProjects(input.projects);
  const registeredDesktopRuntimeById = buildRegisteredDesktopRuntimeMap({
    registeredRuntimes: input.registeredRuntimes,
    registeredDevices: input.registeredDevices,
    registeredHosts: input.registeredHosts,
    currentRegisteredRuntimeId: input.currentRegisteredRuntimeId,
    currentRegisteredDeviceKey: input.currentRegisteredDeviceKey,
  });
  const inventoryByRuntimeId = new Map(
    (input.runtimeInventory ?? []).map((runtime) => [runtime.runtime.id, runtime]),
  );
  const labelContext = {
    savedEnvironmentRegistry: input.savedEnvironmentRegistry,
    savedEnvironmentRuntimeById: input.savedEnvironmentRuntimeById,
    registeredDesktopRuntimeById,
    inventoryRuntimeById: inventoryByRuntimeId,
  };

  const groups: RuntimeProjectGroup[] = [];
  if (input.showLocalEnvironment) {
    groups.push({
      environmentId: null,
      device: null,
      label: input.localEnvironmentLabel ?? "This Device",
      kind: "desktop-local",
      projects: localProjects,
      showKindIcon: true,
    });
  }

  const remoteEnvironmentIds = buildRemoteEnvironmentIdSet({
    remoteProjectGroups,
    savedExecutableEnvironmentIds: getSavedExecutableEnvironmentIds(input),
    registeredDesktopRuntimeById,
    cloudRuntimeEnvironmentIds: input.cloudRuntimeEnvironmentIds,
  });

  for (const environmentId of sortRuntimeEnvironmentIds(remoteEnvironmentIds, labelContext)) {
    const label = resolveRuntimeEnvironmentLabel(environmentId, labelContext);
    const runtimeState = input.savedEnvironmentRuntimeById[environmentId];
    const registeredRuntime = registeredDesktopRuntimeById.get(environmentId);
    const inventoryRuntime = inventoryByRuntimeId.get(environmentId);
    const status = resolveRuntimeGroupStatus({ registeredRuntime, inventoryRuntime });
    groups.push({
      environmentId,
      device: registeredRuntime?.device ?? null,
      label,
      kind: resolveRuntimeEnvironmentKind({
        label,
        platformOs: registeredRuntime?.platformOs ?? runtimeState?.descriptor?.platform?.os ?? null,
      }),
      projects: remoteProjectGroups.get(environmentId) ?? [],
      showKindIcon: true,
      ...(status ? { status } : {}),
    });
  }

  return groups;
}
